import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { afterAuthPath } from "@/lib/guest";
import { toast } from "sonner";
import { Btn, Field } from "@/components/kit";
import AuthLayout from "@/components/AuthLayout";
import { useAuth } from "@/context/auth";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export { AuthLayout };

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState(localStorage.getItem("auth.lastEmail") || "");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (email.includes("@") ? !EMAIL_RE.test(email.trim()) : email.replace(/\D/g, "").length < 10) next.email = "Enter a valid email address or 10-digit mobile number.";
    if (password.length < 6) next.password = "Password must be at least 6 characters.";
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length) return;
    setLoading(true);
    try {
      await login(email.trim(), password);
      localStorage.setItem("auth.lastEmail", email.trim());
      toast.success("Welcome back! You're signed in.");
      nav(afterAuthPath());
    } catch (err) {
      setFormError(err.message || "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout topRight={<>New to TOTO?&nbsp;<Link to="/register" className="t-link text-[14px]" data-testid="top-go-to-register">Create an account</Link></>}>
      <h1 className="text-[28px] lg:text-[34px] font-extrabold" data-testid="login-title">Welcome</h1>
      <p className="text-[15px] text-[#475569] mt-1 mb-7" data-testid="login-subtitle">Sign in to continue to your network.</p>
      {(
        <form onSubmit={submit} className="flex flex-col gap-5">
          <Field label="Email or mobile number" icon={Mail} type="text" inputMode="email" placeholder="you@company.com or 9876543210" value={email} onChange={(e) => setEmail(e.target.value)} error={errors.email} autoComplete="username" testid="login-email" />
          <Field label="Password" icon={Lock} type={show ? "text" : "password"} placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)} error={errors.password} autoComplete="current-password" testid="login-password"
            right={<button type="button" className="w-10 h-10 flex items-center justify-center text-[#64748B]" onClick={() => setShow(!show)} data-testid="login-password-toggle">{show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>} />
          <Link to="/forgot-password" className="t-link self-end -mt-2" data-testid="go-to-forgot">Forgot password?</Link>
          {formError && <p className="text-[14px] text-[#DC2626] -mt-2" data-testid="login-error">{formError}</p>}
          <Btn type="submit" loading={loading} testid="login-submit">Login</Btn>
        </form>
      )}
      <div className="flex items-center gap-3 my-6 text-[14px] font-semibold text-[#64748B]"><span className="flex-1 h-px bg-[#E2E8F0]" />or<span className="flex-1 h-px bg-[#E2E8F0]" /></div>
      <Link to="/try" className="t-btn t-btn-outline w-full gap-2 text-[#0000EC]" data-testid="try-without-login"><Camera className="w-5 h-5" />Try Without Login</Link>
      <p className="mt-2 text-center text-[13px] text-[#64748B]" data-testid="try-without-login-caption">Scan one card free — no account needed.</p>
      <div className="flex items-center gap-3 my-7 text-[12px] font-semibold text-[#94A3B8]"><span className="flex-1 h-px bg-[#E2E8F0]" />NEW TO TOTO?<span className="flex-1 h-px bg-[#E2E8F0]" /></div>
      <Link to="/register" className="t-btn t-btn-outline w-full" data-testid="go-to-register">Create a free account</Link>
      <p className="mt-6 text-center text-[13px] text-[#64748B]" data-testid="login-legal">
        By signing in, you agree to our{" "}
        <Link to="/legal/terms" className="font-semibold text-[#0000EC] hover:underline" data-testid="login-terms-link">Terms of Service</Link>{" "}and{" "}
        <Link to="/legal/privacy" className="font-semibold text-[#0000EC] hover:underline" data-testid="login-privacy-link">Privacy Policy</Link>.
      </p>
    </AuthLayout>
  );
}
